/** biome-ignore-all lint/correctness/useExhaustiveDependencies: TODO: fix useEffect */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Divider, Row } from 'antd';
import { cloneDeep, groupBy, maxBy } from 'es-toolkit';

import { routeFileSelector } from '@/utilz';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import ContentSpinner from '@/views/components/Spinners/ContentSpinner';

import { LADCovertDatesFromMomentToAPIType } from '../../../../../../utilz/PreClaimsUtils';
import Button from '../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import { useGetLADClaimFiles } from '../../../hooks';
import { useGetPreClaimsDocumentTypes } from '../../../PreClaims/hooks';
import { useGetClaimDetails, useSaveClaim } from '../../hooks';
import { getTabDisplayState } from '../utils/getTabDisplayState';
import LADClaimsFilesCard from './LADClaimsFilesCard';

const LADClaimsFilesTab = ({ claimKey }) => {
  const { data: claimDetails } = useGetClaimDetails(claimKey);
  const { data: documentTypeOptions } = useGetPreClaimsDocumentTypes();
  const {
    data: claimFilesData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useGetLADClaimFiles({ ClaimKey: claimKey });
  const { mutate: saveClaim } = useSaveClaim();
  const [newFiles, setNewFiles] = useState([]);
  const [fileEditModes, setFileEditModes] = useState({});
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const claimFiles = useMemo(() => {
    if (!claimFilesData) {
      return { data: [], totalRows: 0 };
    }
    return {
      data: claimFilesData.files ?? [],
      totalRows: claimFilesData.totalRows || 0,
    };
  }, [claimFilesData]);

  const convertFromAPIToLocal = (files) => {
    // TODO: use native Object.groupBy
    const filesL = groupBy(files, (item) => item.documentType);
    const arrayFiles = [];
    for (const key in filesL) {
      arrayFiles.push({ ...filesL[key][0], fileList: filesL[key] });
    }
    return arrayFiles;
  };
  const filesList = useMemo(() => {
    const groupedFiles = convertFromAPIToLocal(claimFiles.data.map((data) => ({ ...data, isNew: false }))).map(
      (row) => ({
        ...row,
        editMode: fileEditModes[row.sequence] ?? row.editMode ?? false,
      }),
    );
    return [...newFiles, ...groupedFiles];
  }, [claimFiles.data, fileEditModes, newFiles]);
  const { showContent, showEmpty, showFullLoader, showNextPageLoader } = useMemo(
    () =>
      getTabDisplayState({
        hasRows: filesList.length > 0,
        isFetching,
        isFetchingNextPage,
        isLoading,
      }),
    [filesList.length, isFetching, isFetchingNextPage, isLoading],
  );

  const handleNextPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const setEditMode = (index, val) => {
    const file = filesList[index];
    if (!file) {
      return;
    }
    if (file.isNew) {
      setNewFiles((prev) => prev.map((row) => (row.sequence === file.sequence ? { ...row, editMode: val } : row)));
      return;
    }
    setFileEditModes((prev) => ({ ...prev, [file.sequence]: val }));
  };

  const handleCancel = (index, isNew) => {
    const file = filesList[index];
    if (!file) {
      return;
    }
    if (isNew) {
      setNewFiles((prev) => prev.filter((row) => row.sequence !== file.sequence));
    } else {
      setFileEditModes((prev) => ({ ...prev, [file.sequence]: false }));
    }
  };

  const handleSubmit = (data, index) => {
    if (!claimDetails) {
      return;
    }
    const selectedFile = filesList[index];
    if (!selectedFile) {
      return;
    }
    let payload = cloneDeep(claimDetails);
    let filesListL = cloneDeep(filesList);
    filesListL = [data];
    payload = LADCovertDatesFromMomentToAPIType(payload);
    payload.claimFile = convertLocalToAPI(filesListL);
    payload.section = 'FILES';
    saveClaim(payload, {
      onSuccess: () => {
        if (selectedFile.isNew) {
          setNewFiles((prev) => prev.filter((row) => row.sequence !== selectedFile.sequence));
        } else {
          setFileEditModes((prev) => ({ ...prev, [selectedFile.sequence]: false }));
        }
      },
    });
  };

  const convertLocalToAPI = (files) => {
    const arrayFiles = [];
    files.forEach(({ fileList, dirtyFlag, isNew, editMode, ...data }) => {
      const selectedDocTypeOption = documentTypeOptions?.find((opt) => String(opt.value) === String(data.documentType));
      const description = selectedDocTypeOption?.label || data.description;

      fileList.forEach(({ sequence, file, isNew, editMode, ...rest }) => {
        arrayFiles.push({
          ...rest,
          ...data,
          description,
          dirtyFlag: rest.dirtyFlag || dirtyFlag || 'N',
          fileContent: file,
          sequence,
        });
      });
    });
    return arrayFiles;
  };

  const handleDelete = (index) => {
    const selectedFile = filesList[index];
    if (!selectedFile) {
      return;
    }
    if (selectedFile.isNew) {
      setNewFiles((prev) => prev.filter((row) => row.sequence !== selectedFile.sequence));
      return;
    }
    if (!claimDetails) {
      return;
    }
    let payload = cloneDeep(claimDetails);
    const fileDetails = cloneDeep(selectedFile);
    fileDetails.fileList = fileDetails.fileList.map((row) => ({
      ...row,
      dirtyFlag: 'D',
    }));
    payload = LADCovertDatesFromMomentToAPIType(payload);
    payload.claimFile = convertLocalToAPI([fileDetails]);
    payload.section = 'FILES';
    saveClaim(payload);
  };

  const handleAddNew = () => {
    let nextSequence = maxBy([...claimFiles.data, ...newFiles], ({ sequence }) => Number(sequence));
    nextSequence = nextSequence ? nextSequence.sequence : 0;
    setNewFiles((prev) => [
      {
        claimKey: claimDetails?.claimKey,
        description: undefined,
        documentType: undefined,
        editMode: true,
        fileComments: undefined,
        fileDesc: undefined,
        fileName: undefined,
        isNew: true,
        originalFileName: undefined,
        sequence: nextSequence + 1,
      },
      ...prev,
    ]);
  };

  return (
    <div className="flex-direction-column d-flex h-100 overflow-hidden">
      <Row className="align-items-center pt-15 px-30" justify="space-between">
        <Col>
          <PrimaryLabel className="fw-700 f-18">Upload/View Files</PrimaryLabel>
        </Col>
        <Col>
          <Row justify="end" gutter={16}>
            <Col>
              <Button onClick={handleAddNew}>Add New Document</Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mt-0 my-16 ml-n16 vw-100" />
      <div className="flex-1 custom-scroll-bar">
        {showFullLoader ? (
          <div className="px-30 py-10 flex-1" data-testid="lad-files-loading">
            <ContentSpinner />
          </div>
        ) : showContent ? (
          <ScrollPagination
            className="px-30 py-10 flex-1"
            loadMore={handleNextPage}
            isLoading={showNextPageLoader}
            hasMore={isFetchingNextPage ? false : hasNextPage}
            scrollOffset={30}
          >
            {filesList.map((row, index) => (
              <LADClaimsFilesCard
                fileDetails={row}
                key={row.sequence}
                index={index}
                editMode={Boolean(row.editMode)}
                setEditMode={setEditMode}
                handleCancel={handleCancel}
                handleSubmit={handleSubmit}
                handleDelete={handleDelete}
                fileParamName="file"
              />
            ))}
          </ScrollPagination>
        ) : showEmpty ? (
          <EmptyRecords
            title={t('translation:default.documentManager.noFoundText')}
            descriptionOne={t('translation:claims.emptyViewFiles')}
            descriptionTwo=""
          />
        ) : null}
      </div>
    </div>
  );
};

export default LADClaimsFilesTab;
